import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      console.log('Attempting login with credentials:', credentials);
      const response = await api.login(credentials);
      console.log('Login response:', response);
      if (response?.data?.token) {
        localStorage.setItem('token', response.data.token);
        return {
          ...response.data.user,
          passwordResetRequired: response.data.user.passwordResetRequired
        };
      } else {
        return rejectWithValue('Login failed: Unexpected response format');
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);

      // Extract the actual error message from the response
      let errorMessage = 'Login failed';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Provide user-friendly error messages for network issues
      if (errorMessage.toLowerCase().includes('network') || errorMessage.toLowerCase().includes('connection')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      return rejectWithValue(errorMessage);
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/users/refresh-token');
      if (response && response.data && response.data.token) {
        localStorage.setItem('token', response.data.token);
        return response.data.user;
      } else {
        return rejectWithValue('Token refresh failed: Unexpected response format');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      return rejectWithValue(error.response?.data?.message || 'Token refresh failed');
    }
  }
);

export const updateUserSettings = createAsyncThunk(
  'auth/updateUser',
  async (updates, { rejectWithValue }) => {
    try {
      const response = await api.put('/users/settings', updates);
      if (response && response.data && response.data.user) {
        return response.data.user;
      } else {
        return rejectWithValue('Update failed: Unexpected response format');
      }
    } catch (error) {
      console.error('User update error:', error);
      return rejectWithValue(error.response?.data?.message || 'Update failed');
    }
  }
);

export const completeOnboarding = createAsyncThunk(
  'auth/completeOnboarding',
  async ({ token, ...payload }, { rejectWithValue }) => {
    try {
      const response = await api.completeOnboarding(token, payload);
      if (response?.data?.token) {
        localStorage.setItem('token', response.data.token);
        return {
          ...response.data.user,
          passwordResetRequired: response.data.user.passwordResetRequired
        };
      }
      return rejectWithValue('Onboarding failed: Unexpected response format');
    } catch (error) {
      const errorMessage = error.response?.data?.error
        || error.response?.data?.message
        || error.message
        || 'Could not create account';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchUserProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      console.log('fetchUserProfile: Making API call to /users/profile');
      const response = await api.get('/users/profile');
      console.log('fetchUserProfile: API response:', response);
      if (response && response.data) {
        console.log('fetchUserProfile: Returning user data:', response.data);
        return response.data;
      } else {
        console.log('fetchUserProfile: Unexpected response format:', response);
        return rejectWithValue('Profile fetch failed: Unexpected response format');
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
      return rejectWithValue(error.response?.data?.message || 'Profile fetch failed');
    }
  }
);

export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { dispatch }) => {
    console.log('Initializing auth state');
    const token = localStorage.getItem('token');
    if (token) {
      console.log('Found existing token, fetching user profile');
      try {
        const result = await dispatch(fetchUserProfile()).unwrap();
        console.log('initializeAuth: Profile fetch result:', result);
        return result;
      } catch (error) {
        console.error('Failed to fetch profile during initialization:', error);
        localStorage.removeItem('token');
        return null;
      }
    }
    console.log('No token found during initialization');
    return null;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: localStorage.getItem('token'),
    status: 'idle',
    error: null,
    passwordResetRequired: false,
    isInitialized: false
  },
  reducers: {
    logout: (state) => {
      console.log('Logging out user');
      state.user = null;
      state.token = null;
      state.passwordResetRequired = false;
      state.isInitialized = true;
      state.error = null; // Clear any error state
      localStorage.removeItem('token');
    },
    patchUser: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeAuth.pending, (state) => {
        console.log('Auth initialization pending');
        state.status = 'loading';
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        console.log('Auth initialization complete:', action.payload);
        state.status = 'succeeded';
        state.user = action.payload;
        state.isInitialized = true;
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        console.error('Auth initialization failed:', action.error);
        state.status = 'failed';
        state.error = action.error.message;
        state.isInitialized = true;
      })
      .addCase(loginUser.pending, (state) => {
        console.log('Login pending');
        state.status = 'loading';
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        console.log('Login succeeded:', action.payload);
        state.status = 'succeeded';
        state.user = action.payload;
        state.token = localStorage.getItem('token');
        state.passwordResetRequired = action.payload.passwordResetRequired || false;
        state.error = null; // Clear any error state on successful login
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(completeOnboarding.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(completeOnboarding.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
        state.token = localStorage.getItem('token');
        state.passwordResetRequired = action.payload.passwordResetRequired || false;
        state.error = null;
      })
      .addCase(completeOnboarding.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.user = action.payload;
        state.token = localStorage.getItem('token');
      })
      .addCase(updateUserSettings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateUserSettings.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
      })
      .addCase(updateUserSettings.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      });
  }
});

export const { logout, patchUser } = authSlice.actions;

export default authSlice.reducer;
